import {Directive, Input} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {FilterPersistenceService} from './filter-persistence.service';
import {ActivatedRoute} from '@angular/router';
import {SearchService} from './search.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {updateFormWithValidFields} from '../../util/searchUtil';

@Directive()
export abstract class SearchFilterBase
{
    @Input('autoRefresh') protected autoRefresh = false;
    @Input('autoRefreshRate') protected autoRefreshRate = 10000;
    @Input('pageSize') protected pageSize = 10;

    protected routeKey: string;
    protected _activeTab: string = '';
    protected interval: any;

    protected constructor(
        protected searchDataService: SearchService,
        protected ngxLoader: NgxUiLoaderService,
        protected filterPersistenceService: FilterPersistenceService,
        protected route: ActivatedRoute,
    )
    {
    }

    abstract getSearchCriteria(): any;

    abstract setFilters(): void;

    /**
     * Initializes the search page by setting up filters, triggering an initial search,
     * and optionally enabling auto-refresh.
     *
     * - Collapses the search view by default.
     * - Subscribes to route changes to update the search filters and perform a search.
     * - If auto-refresh is enabled, periodically refreshes the search results.
     */
    initializeSearchPage(): void
    {
        this.searchDataService.searchViewCollapsed = true;

        this.route.url.subscribe((segments) =>
        {
            this.routeKey = segments.map((s) => s.path).join('/');
            this.setFilters();
            this.search();
        });

        if (this.autoRefresh)
        {
            this.interval = setInterval(() =>
            {
                this.refresh();
            }, this.autoRefreshRate);
        }
    }

    refresh(): void
    {
        this.searchDataService.searchableCriteria = this.getSearchCriteria();
        this.saveCurrentFilters();
        this.searchDataService.refreshSearch(this.getSearchCriteria());
    }

    search(): void
    {
        this.ngxLoader.start();
        this.refresh();
        this.ngxLoader.stop();
    }

    /**
     * Clears the specified date input field in the given form and ensures the UI reflects the change.
     *
     * This method resets the form control by setting its value to `null`, ensuring that the date input field
     * is properly cleared. It addresses cases where UI updates may not immediately take effect, particularly
     * in some browsers like Chrome.
     *
     * @param {UntypedFormGroup} form - The form containing the date input field.
     * @param {string} dateField - The name of the form control and corresponding input field ID.
     */
    clearDateInputField(form: UntypedFormGroup, dateField: string)
    {
        setTimeout(() =>
        {
            form.get(dateField)?.setValue(null);
        });
    }

    /**
     * Saves the current search filters for the active route.
     *
     * This method retrieves the current search criteria, adds the active tab information,
     * and stores the filters using the `filterPersistenceService`. This ensures that
     * the filter preferences persist across interactions.
     */
    saveCurrentFilters()
    {
        this.filterPersistenceService.setFilters(
            this.routeKey,
            {...this.getSearchCriteria(), activeTab: this._activeTab}
        );
    }

    /**
     * Applies saved filters to the provided form and updates the UI accordingly.
     *
     * This method retrieves saved search criteria, updates the active tab state,
     * and applies the criteria to the form and search data. Additionally, it allows
     * optional extra updates specific to certain filters.
     *
     * @template T - The type of search criteria being applied.
     * @param {UntypedFormGroup} form - The form where filters are applied.
     * @param {(criteria: T) => void} setCriteria - Function to set the search criteria.
     * @param {(criteria: T) => void} [updateFormFields] - Optional function for additional form updates.
     */
    applyFilters<T>(
        form: UntypedFormGroup,
        setCriteria: (criteria: T) => void,
        updateFormFields?: (criteria: T) => void
    ): void
    {
        const savedCriteria: any = this.filterPersistenceService.getFilters(this.routeKey);
        if (!savedCriteria)
        {
            return;
        }

        this._activeTab = savedCriteria.activeTab;
        delete savedCriteria.activeTab;

        this.searchDataService.searchViewCollapsed = !this._activeTab;
        setCriteria(savedCriteria);
        updateFormWithValidFields(form, savedCriteria);

        updateFormFields?.(savedCriteria);
    }
}